import React from 'react';
import cn from 'classnames';

type PhoneInputProps = {
  name: string;
  label: string;
  value: string;
  onChange: (digits: string, isValid: boolean) => void;
  required?: boolean;
  disabled?: boolean;
  showRequiredErrors?: boolean;
  wrapperClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  errorClassName?: string;
  dataTestId?: string;
  placeholder?: string;
};

const getDigitsOnly = (value: string) => value.replace(/\D/g, '').slice(0, 10);

const formatPhone = (digits: string) => {
  const sanitized = getDigitsOnly(digits);

  if (sanitized.length === 0) return '';
  if (sanitized.length <= 3) return `(${sanitized}`;
  if (sanitized.length <= 6) return `(${sanitized.slice(0, 3)}) ${sanitized.slice(3)}`;
  return `(${sanitized.slice(0, 3)}) ${sanitized.slice(3, 6)}-${sanitized.slice(6)}`;
};

const PhoneInput: React.FC<PhoneInputProps> = ({
  name,
  label,
  value,
  onChange,
  required = false,
  disabled = false,
  showRequiredErrors = false,
  wrapperClassName,
  labelClassName,
  inputClassName,
  errorClassName,
  dataTestId,
  placeholder = '(___) ___-____',
}) => {
  const [touched, setTouched] = React.useState(false);

  const digits = getDigitsOnly(value || '');
  const formattedValue = formatPhone(digits);

  const requiredError = required && (touched || showRequiredErrors) && digits.length === 0;
  const invalidPhoneError = (touched || showRequiredErrors) && digits.length > 0 && digits.length < 10;
  const hasError = requiredError || invalidPhoneError;

  const helperText = requiredError
    ? `${label.replace(/:\s*$/, '')} is required`
    : invalidPhoneError
    ? 'Please enter a valid 10-digit phone number.'
    : null;

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const updatedDigits = getDigitsOnly(event.target.value);
    onChange(updatedDigits, updatedDigits.length === 10);
  };

  return (
    <div className={cn('self-stretch flex flex-col items-start', wrapperClassName)} data-testid={dataTestId}>
      <label
        className={cn('pb-2 text-left text-sm font-semibold leading-5 text-blue-950', labelClassName, hasError && 'text-red-600')}
        htmlFor={name}
      >
        {label}
        {required && (
          <span aria-hidden className='text-red-600 text-xl'>
            {' '}
            *
          </span>
        )}
      </label>
      <input
        id={name}
        name={name}
        type='tel'
        inputMode='numeric'
        value={formattedValue}
        onChange={handleInputChange}
        onBlur={() => setTouched(true)}
        placeholder={placeholder}
        disabled={disabled}
        aria-required={required || undefined}
        aria-invalid={hasError || undefined}
        aria-describedby={hasError ? `${name}-error` : undefined}
        className={cn(
          'w-full h-11 px-3 py-5 rounded-sm outline outline-1 outline-offset-[-1px] outline-zinc-300 hover:outline-sky-800 hover:bg-blue-50',
          hasError && '!border-red-600 !outline-red-600 focus:!border-red-600 focus:!ring-red-200',
          inputClassName
        )}
      />
      {helperText && (
        <p id={`${name}-error`} className={cn('mt-1 text-left text-sm text-red-600', errorClassName)} role='alert'>
          {helperText}
        </p>
      )}
    </div>
  );
};

export default PhoneInput;
